import { useCallback, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { checkBrowserAllowClick } from '~utilities';
import { useDispatch } from '~Hooks';
import { addToast, ToastPlacement } from '~actions';
import { useStorage } from '~Components/Storage';

interface Props {
  disabled?: boolean;
  disabledToast?: string;
  /** Where the disabled toast appears. Use 'top' for buttons in a modal's bottom bar, so the toast doesn't cover them */
  disabledToastPlacement?: ToastPlacement;
  onClick: () => void;
}

/**
 * This hook is used to handle the touch events of a button.
 *
 * It returns the props that should be spread onto the button.
 *
 * You then use those props on the button like this:
 *
 * @usage
 * const Button = styled.div<{ touching: boolean }>`
 *  background: ${({ touching }) => (touching ? 'red' : 'blue')};
 * `;
 *
 * const button = useButton({ onClick: () => {} }});
 *
 * <Button {...button} />
 */
export const useButton = (props: Props) => {
  const dispatch = useDispatch();
  const [storage] = useStorage();

  const isTouching = useRef(false);
  const [touching, setTouching] = useState<boolean>(false);

  const allowBrowserClick = checkBrowserAllowClick(storage.allowBrowserClick);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<any>) => {
      e.stopPropagation();
      e.preventDefault();

      isTouching.current = true;

      setTouching(true);
    },
    [props.onClick],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent<any>) => {
      e.stopPropagation();
      e.preventDefault();

      setTouching(false);

      if (isTouching.current) {
        // Show toast if button is disabled and has a disabledToast message
        if (props.disabled && props.disabledToast) {
          dispatch(
            addToast(
              props.disabledToast,
              true,
              null,
              false,
              undefined,
              false,
              props.disabledToastPlacement,
            ),
          );
        } else if (!props.disabled) {
          // Only execute onClick if button is not disabled
          props.onClick();
        }
      }

      isTouching.current = false;
    },
    [
      touching,
      props.onClick,
      props.disabled,
      props.disabledToast,
      props.disabledToastPlacement,
      dispatch,
    ],
  );

  const onClick = useCallback(
    // HTMLElement rather than HTMLDivElement so these props can also be spread
    // onto a real <button>. Widening the parameter keeps every existing
    // div-based caller valid.
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      // Return early if not a touch interface and not allowed to click through
      if ((window.useTouchInterface || !isTouching.current) && !allowBrowserClick) {
        return;
      }

      isTouching.current = false;
      setTouching(false);

      // Show toast if button is disabled and has a disabledToast message
      if (props.disabled && props.disabledToast) {
        dispatch(
          addToast(
            props.disabledToast,
            true,
            null,
            false,
            undefined,
            false,
            props.disabledToastPlacement,
          ),
        );
      } else if (!props.disabled) {
        // Only execute onClick if button is not disabled
        props.onClick();
      }
    },
    [
      props.onClick,
      props.disabled,
      props.disabledToast,
      props.disabledToastPlacement,
      dispatch,
      allowBrowserClick,
    ],
  );

  const handlers = useSwipeable({
    delta: 20,
    onSwiping: () => {
      isTouching.current = false;
      setTouching(false);
    },
  });

  return {
    ...handlers,
    onTouchStart,
    onTouchEnd,
    onClick,
    touching,
    disabled: props.disabled,
    disabledToast: props.disabledToast,
  };
};
